// VAT201 calculation. Reads posted journal lines for an org+period, joins
// to tax_codes via line.tax_code_id, and aggregates by output/input × rate.
//
// SARS VAT201 box mapping (simplified — full VAT201 has ~30 fields, this
// covers the core boxes a typical owner-operator needs):
//   Box 1  — Standard-rated supplies (output, rate=15%): NET (sales)
//   Box 1A — Output VAT on Box 1 (NET × 15%)
//   Box 2  — Zero-rated supplies
//   Box 3  — Exempt + non-supplies
//   Box 14 — Standard-rated purchases (input): NET
//   Box 14A— Input VAT on Box 14
//   Box 18 — Net VAT (Output − Input). Positive = payable to SARS, negative = refund.
//
// We trust each journal_line's tax_code_id to be the correct classification
// for the underlying transaction. Lines without a tax_code are ignored.

import { supabase } from './supabase';
import { r2 } from './posting';

export interface Vat201 {
  period_from: string;
  period_to: string;
  // Outputs (sales side)
  box_1_net: number;
  box_1a_vat: number;
  box_2_net: number;
  box_3_net: number;
  output_vat_total: number;
  // Inputs (purchases side)
  box_14_net: number;
  box_14a_vat: number;
  input_vat_total: number;
  // Net
  box_18_payable: number;
  is_refund: boolean;
}

interface AggRow {
  tax_code_id: string;
  code: string;
  label: string;
  rate: number;
  is_output: boolean;
  is_input: boolean;
  // Net = debit-side for purchases (excludes the VAT input line itself),
  // credit-side for sales (excludes the VAT output line itself).
  // We compute by summing debits/credits on revenue/expense rows tagged
  // with the tax_code.
  net_credits: number;   // sales side (revenue accounts)
  net_debits: number;    // purchases side (expense/asset accounts)
}

export async function calculateVat201(orgId: string, fromISO: string, toISO: string): Promise<Vat201 | { error: string }> {
  // Pull tax codes for this org.
  const { data: tcs, error: tce } = await supabase
    .from('dodobooks_tax_codes')
    .select('*')
    .eq('org_id', orgId);
  if (tce) return { error: tce.message };

  // Pull all journal lines in the period that have a tax_code_id, joined
  // to their journal (for the date filter and org filter) and to their
  // account (so we know whether the line is on a revenue / expense / VAT side).
  const { data, error } = await supabase
    .from('dodobooks_journal_lines')
    .select(`
      id, debit, credit, tax_code_id,
      account:dodobooks_accounts!inner(id, account_type, sars_role),
      journal:dodobooks_journals!inner(id, org_id, date, is_posted)
    `)
    .not('tax_code_id', 'is', null)
    .eq('journal.org_id', orgId)
    .eq('journal.is_posted', true)
    .gte('journal.date', fromISO)
    .lte('journal.date', toISO);
  if (error) return { error: error.message };

  // Index tax codes for lookup.
  const tcMap = new Map<string, AggRow>();
  for (const t of tcs ?? []) {
    tcMap.set(t.id, {
      tax_code_id: t.id,
      code: t.code,
      label: t.label,
      rate: Number(t.rate),
      is_output: !!t.is_output,
      is_input: !!t.is_input,
      net_credits: 0,
      net_debits: 0,
    });
  }

  for (const row of (data ?? []) as any[]) {
    const tc = tcMap.get(row.tax_code_id);
    if (!tc) continue;
    const acctType = row.account?.account_type as string | undefined;
    const sarsRole = row.account?.sars_role as string | null | undefined;
    // Ignore the VAT control account lines themselves — they're the
    // "tax" leg of the journal, not the "net" leg.
    if (sarsRole === 'vat_input' || sarsRole === 'vat_output') continue;
    if (acctType === 'revenue') {
      // For sales: net is on the CREDIT side; refunds/credit-notes would be on debit.
      tc.net_credits += Number(row.credit) - Number(row.debit);
    } else if (acctType === 'cogs' || acctType === 'expense' || acctType === 'asset') {
      // For purchases: net is on the DEBIT side.
      tc.net_debits += Number(row.debit) - Number(row.credit);
    }
  }

  let box_1_net = 0, box_1a_vat = 0;
  let box_2_net = 0, box_3_net = 0;
  let box_14_net = 0, box_14a_vat = 0;

  for (const tc of tcMap.values()) {
    // OUTPUT side (sales)
    if (tc.is_output && tc.net_credits > 0) {
      if (tc.rate > 0) {
        box_1_net += tc.net_credits;
        box_1a_vat += tc.net_credits * tc.rate;
      } else if (tc.code === 'ZER' || /zero/i.test(tc.label)) {
        box_2_net += tc.net_credits;
      } else {
        box_3_net += tc.net_credits;
      }
    }
    // INPUT side (purchases)
    if (tc.is_input && tc.net_debits > 0 && tc.rate > 0) {
      box_14_net += tc.net_debits;
      box_14a_vat += tc.net_debits * tc.rate;
    }
  }

  const output_vat_total = r2(box_1a_vat);
  const input_vat_total = r2(box_14a_vat);
  const box_18_payable = r2(output_vat_total - input_vat_total);

  return {
    period_from: fromISO,
    period_to: toISO,
    box_1_net: r2(box_1_net),
    box_1a_vat: r2(box_1a_vat),
    box_2_net: r2(box_2_net),
    box_3_net: r2(box_3_net),
    output_vat_total,
    box_14_net: r2(box_14_net),
    box_14a_vat: r2(box_14a_vat),
    input_vat_total,
    box_18_payable: r2(Math.abs(box_18_payable)),
    is_refund: box_18_payable < 0,
  };
}

/** Snap a date to the start of its VAT period (assumes two-monthly Mar/May/Jul/...). */
export function vatPeriodRange(d: Date, period: 'monthly' | 'two_monthly' | 'six_monthly' | 'annual'): { from: string; to: string } {
  const y = d.getFullYear();
  const m = d.getMonth(); // 0-indexed
  const toISO = (yy: number, mm: number, dd: number) => `${yy}-${String(mm + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  if (period === 'monthly') {
    const last = new Date(y, m + 1, 0).getDate();
    return { from: toISO(y, m, 1), to: toISO(y, m, last) };
  }
  if (period === 'two_monthly') {
    // SA two-monthly: pairs Mar-Apr, May-Jun, Jul-Aug, Sep-Oct, Nov-Dec, Jan-Feb.
    // Pair start month relative to March (mar=2 in JS = 0-indexed feb=1, so mar=2).
    // Compute offset from March, pair-floor it.
    const offset = ((m - 2) + 12) % 12;   // 0 for Mar, 1 for Apr, ... 11 for Feb
    const pairStart = Math.floor(offset / 2) * 2; // 0,2,4,6,8,10
    const startMonth = ((pairStart + 2) % 12);
    const startYear = m >= startMonth ? y : (startMonth < 2 ? y : y - 1);
    const endMonth = (startMonth + 1) % 12;
    const endYear = endMonth >= startMonth ? startYear : startYear + 1;
    const lastDay = new Date(endYear, endMonth + 1, 0).getDate();
    return { from: toISO(startYear, startMonth, 1), to: toISO(endYear, endMonth, lastDay) };
  }
  if (period === 'six_monthly') {
    // Mar-Aug and Sep-Feb pairs.
    const isFirstHalf = m >= 2 && m <= 7;
    if (isFirstHalf) return { from: toISO(y, 2, 1), to: toISO(y, 7, 31) };
    if (m >= 8) return { from: toISO(y, 8, 1), to: toISO(y + 1, 1, new Date(y + 1, 2, 0).getDate()) };
    return { from: toISO(y - 1, 8, 1), to: toISO(y, 1, new Date(y, 2, 0).getDate()) };
  }
  // annual: Mar–Feb SA tax year.
  if (m >= 2) return { from: toISO(y, 2, 1), to: toISO(y + 1, 1, new Date(y + 1, 2, 0).getDate()) };
  return { from: toISO(y - 1, 2, 1), to: toISO(y, 1, new Date(y, 2, 0).getDate()) };
}
